import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '@kop/ui';
import { RefreshCw } from 'lucide-react';
import { HubHeader, HubFilters } from '../hub-shell/hub-shell';
import { FeatureLoading } from '../feature-skeleton';
import { useWorkspaceReady } from '../../auth/workspace-entry-context';
import {
  loadMonthlyMeeting,
  peekMonthlyMeeting,
  previousMeetingMonth,
  type MonthlyMeetingRead,
} from './monthly-meeting-read';
import { MeetingTable, MonthlyMeetingReview } from './monthly-meeting-review';
import { onSensitiveMemoryCacheClear } from '../../runtime/sensitive-cache-lifecycle';
import { loadHeadquartersMeetingSites } from '../communications/meeting-minutes-repository';
import './headquarters-dashboard.css';

const number = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('ko-KR'));
const cacheKey = (uid: string, siteId: string | null, month: string) => `${uid}:${siteId ?? 'no-site'}:${month}`;
type HeadquartersDashboardProps = {
  uid: string;
  siteId: string | null;
  featureIds: readonly string[];
  onOpen: (id: string, readSiteId?: string) => void;
};

export function HeadquartersDashboard(props: HeadquartersDashboardProps) {
  return <HeadquartersSiteSelection {...props} key={`${props.uid}:${props.siteId ?? 'hq'}`} />;
}

function HeadquartersSiteSelection(props: HeadquartersDashboardProps) {
  const [sites, setSites] = useState<readonly Readonly<{ siteId: string; displayName: string }>[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const generation = useRef(0);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const run = ++generation.current;
    setSites([]);
    setSelected(null);
    setError(false);
    void loadHeadquartersMeetingSites().then((choices) => {
      if (run !== generation.current) return;
      setSites(choices);
      setSelected(choices.some((site) => site.siteId === props.siteId) ? props.siteId : null);
    }).catch(() => { if (run === generation.current) setError(true); });
    return () => { generation.current += 1; };
  }, [props.uid, props.siteId, revision]);
  useEffect(() => {
    const unsubscribe = onSensitiveMemoryCacheClear(() => {
      generation.current += 1;
      setSites([]);
      setSelected(null);
      setError(false);
    });
    return () => { unsubscribe(); };
  }, []);
  const chosen = sites.find((site) => site.siteId === selected);
  const selector = <>
    <label className="home-tools">사업장
      <select className="home-control" aria-label="본사 조회 사업장" value={chosen?.siteId ?? ''}
        onChange={(event) => setSelected(event.target.value || null)}>
        <option value="">사업장을 선택해 주세요</option>
        {sites.map((site) => <option key={site.siteId} value={site.siteId}>{site.displayName}</option>)}
      </select>
    </label>
    {error && <span role="alert">조회 가능한 사업장을 불러오지 못했습니다.</span>}
  </>;
  return <HeadquartersSiteDashboard {...props} key={`${props.uid}:${chosen?.siteId ?? 'no-site'}`}
    siteId={chosen?.siteId ?? null} siteLabel={chosen?.displayName ?? '사업장 미선택'} siteSelector={selector}
    onRefreshSites={() => setRevision((value) => value + 1)} />;
}

function HeadquartersSiteDashboard({
  uid,
  siteId,
  siteLabel,
  siteSelector,
  onRefreshSites,
  featureIds,
  onOpen,
}: {
  uid: string;
  siteId: string | null;
  siteLabel: string;
  siteSelector: ReactNode;
  onRefreshSites: () => void;
  featureIds: readonly string[];
  onOpen: (id: string, readSiteId?: string) => void;
}) {
  const [month, setMonth] = useState(() =>
      new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' }).slice(0, 7),
    ),
    [tab, setTab] = useState<'home' | 'ledger'>('home'),
    [meeting, setMeeting] = useState(false),
    [reviewMonth, setReviewMonth] = useState(month),
    [data, setData] = useState<MonthlyMeetingRead | null>(() => peekMonthlyMeeting(uid, siteId, month)),
    [busy, setBusy] = useState(() => !peekMonthlyMeeting(uid, siteId, month)),
    [revision, setRevision] = useState(0);
  const lastHandledRevision = useRef(revision);
  const visibleReadKey = useRef<string | null>(peekMonthlyMeeting(uid, siteId, month) ? cacheKey(uid, siteId, month) : null);
  const reveal = useWorkspaceReady();
  const closeMeeting = useCallback(() => setMeeting(false), []);
  useEffect(() => {
    const unsubscribe = onSensitiveMemoryCacheClear(() => {
        visibleReadKey.current = null;
        setData(null);
        setBusy(false);
      });
    return () => {
      unsubscribe();
    };
  }, []);
  useEffect(() => {
    let active = true;
    const key = cacheKey(uid, siteId, month);
    const cached = peekMonthlyMeeting(uid, siteId, month);
    const force = revision !== lastHandledRevision.current;
    lastHandledRevision.current = revision;
    visibleReadKey.current = cached ? key : null;
    setData(cached);
    setBusy(true);
    void loadMonthlyMeeting(uid, siteId, month, force)
      .then((result) => {
        if (active && peekMonthlyMeeting(uid, siteId, month) === result) {
          visibleReadKey.current = key;
          setData(result);
        }
      })
      .catch(() => {
        if (active) setData((current) => (visibleReadKey.current === key ? current : null));
      })
      .finally(() => {
        if (active) {
          setBusy(false);
          reveal();
        }
      });
    return () => {
      active = false;
    };
  }, [uid, siteId, month, revision, reveal]);
  const value = data?.month === month ? data : null,
    ops = value?.operations.value,
    inspection = value?.inspection.value;
  const meetingRows = value?.meetings.value?.records ?? [];
  const latest = meetingRows[0];
  const inspect = () => {
    setReviewMonth(month);
    setMeeting(true);
  };
  const stat = (label: string, v: number | null | undefined, unit: string) => (
    <Button variant="quiet" className="hq-stat" onClick={inspect}>
      <span>{label}</span>
      <strong>
        {number(v)}
        <small>{v == null ? '' : unit}</small>
      </strong>
      <small>{v == null ? '자료 확인 필요' : `${month} 기준`}</small>
    </Button>
  );
  const rows = [
    ['점검 실시', number(inspection?.inspections) + '회', '선택한 기간 실시'],
    ['지적사항', number(inspection?.findings) + '건', '선택한 기간 발생'],
    ['조치 완료', number(inspection?.completed) + '건', '기간 발생 건의 현재 상태'],
    ['미조치', number(inspection?.unresolved) + '건', '기간 발생 건의 현재 상태'],
    ['TBM', number(ops?.tbm?.count) + '회', '월간 실시'],
    ['비일상작업', number(ops?.work?.monthly) + '건', '월간 작업허가'],
    [
      '안전보건교육',
      number(ops?.education?.total) + '회',
      `참석 연인원 ${number(ops?.education?.attendance)}명`,
    ],
    ['비상훈련', number(ops?.drill?.completed) + '건', '실시 결과'],
    [
      '원청 비용',
      value?.budget.value ? number(value.budget.value.aggregates.principal) + '원' : '—',
      '소방·안전관리',
    ],
    [
      '협력업체 비용',
      value?.budget.value ? number(value.budget.value.aggregates.contractor) + '원' : '—',
      '업체 자체 사용',
    ],
  ];
  return (
    <section className="hq-dashboard" data-workspace-dashboard="ledger">
      <HubHeader
        title="종합현황"
        pill={
          <div className="home-tools">
            {siteSelector}
            <input
              type="month"
              className="home-control"
              aria-label="본사 조회 월"
              value={month}
              onChange={(e) => {
                if (e.target.value) setMonth(e.target.value);
              }}
            />
            <Button
              variant="quiet"
              size="compact"
              icon={<RefreshCw size={16} />}
              aria-label="본사 현황 새로고침"
              title="본사 현황 새로고침"
              disabled={busy}
              onClick={() => {
                if (!siteId) onRefreshSites();
                setRevision((n) => n + 1);
              }}
            />
          </div>
        }
      />
      <div className="hq-context">
        <span>{month} 월간 실적</span>
        <span>{siteLabel}</span>
      </div>
      <HubFilters
        items={[
          { value: 'home', label: '종합현황' },
          { value: 'ledger', label: '실적 집계표' },
        ]}
        value={tab}
        onChange={setTab}
        ariaLabel="본사 현황 보기"
      />
      {busy && !value ? (
        <FeatureLoading label="본사 현황 불러오는 중" />
      ) : (
        <div className="hq-content" data-bounded-content>
          {value &&
            [value.operations, value.inspection, value.budget, value.meetings].some(
              (result) => result.state === 'unavailable',
            ) && (
              <p className="hq-read-notice" role="status">
                일부 자료를 조회하지 못했습니다. 확인되지 않은 수치는 —로 표시합니다.
              </p>
            )}
          {tab === 'ledger' ? (
            <section className="hq-panel">
              <header>
                <h2>분야별 실적</h2>
                <Button variant="quiet" className="hq-link" onClick={inspect}>
                  월회의에서 확인 →
                </Button>
              </header>
              <MeetingTable headings={['항목', siteLabel, '집계 기준']} rows={rows} />
            </section>
          ) : (
            <>
              <div className="hq-stat-grid">
                {stat('안전점검', inspection?.inspections, '회')}
                {stat('TBM', ops?.tbm?.count, '회')}
                {stat('비일상작업', ops?.work?.monthly, '건')}
                {stat('지적사항 미조치', inspection?.unresolved, '건')}
              </div>
              <section className="hq-panel">
                <header>
                  <h2>사업장별 점검·조치 현황</h2>
                  <Button variant="quiet" className="hq-link" onClick={() => setTab('ledger')}>
                    실적 집계표 →
                  </Button>
                </header>
                <MeetingTable
                  headings={['사업장', '점검', '지적사항', '완료', '미조치', '결재 대기']}
                  rows={[
                    [
                      siteLabel,
                      number(inspection?.inspections),
                      number(inspection?.findings),
                      number(inspection?.completed),
                      number(inspection?.unresolved),
                      number(inspection?.approval),
                    ],
                  ]}
                />
                <p className="hq-caption">
                  선택한 사업장과 기간에 발생한 건의 현재 조치 상태
                </p>
              </section>
              <div className="hq-bottom-grid">
                <section className="hq-panel">
                  <header>
                    <h2>기간별 관리 현황</h2>
                  </header>
                  <MeetingTable
                    headings={['항목', '현황']}
                    rows={[
                      ['교육 실시', `${number(ops?.education?.total)}회`],
                      ['교육 참석 연인원', `${number(ops?.education?.attendance)}명`],
                      ['비상훈련 결과', `${number(ops?.drill?.completed)}건`],
                      ['반기 위험성평가', `${number(ops?.risk?.total)}건`],
                    ]}
                  />
                </section>
                <section className="hq-panel">
                  <header>
                    <h2>안전보건 월회의</h2>
                    <small>실적·개선·후속조치</small>
                  </header>
                  <div className="hq-meeting-card">
                    <strong>{latest?.title ?? '월회의 자료 취합'}</strong>
                    <p>
                      {latest
                        ? `최근 회의일 ${latest.date}`
                        : value?.meetings.state === 'unavailable'
                          ? '회의록 조회 확인 필요'
                          : '등록된 월간 회의록 없음'}
                    </p>
                    <p>보고 대상월을 선택하고 실적과 회의 내용을 함께 확인합니다.</p>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setReviewMonth(previousMeetingMonth(month));
                        setMeeting(true);
                      }}
                    >
                      월회의 자료 보기 →
                    </Button>
                  </div>
                </section>
                <section className="hq-panel">
                  <header>
                    <h2>본사 업무</h2>
                  </header>
                  <div className="hq-meeting-card">
                    {[
                      ['shared-drive', '안전보건자료실'],
                    ]
                      .filter(([id]) => featureIds.includes(id!))
                      .map(([id, label]) => (
                        <Button
                          key={id}
                          variant="quiet"
                          className="hq-link"
                          onClick={() => onOpen(id!, id === 'shared-drive' ? siteId ?? undefined : undefined)}
                        >
                          {label} →
                        </Button>
                      ))}
                    <p>본사 작성 자료와 공장 실적을 구분해서 관리합니다.</p>
                  </div>
                </section>
              </div>
            </>
          )}
        </div>
      )}
      {meeting && (
        <MonthlyMeetingReview
          uid={uid}
          siteId={siteId}
          initialMonth={reviewMonth}
          onClose={closeMeeting}
          onOpen={onOpen}
          featureIds={featureIds}
        />
      )}
    </section>
  );
}
